import { reactive, readonly } from 'vue'
import { apiFetch } from '../services/api'

/**
 * Singleton composable that loads integration-enabled status for the tenant.
 * Combines workspace-level integrations (GET /settings/integrations) and
 * personal-level integrations (GET /user/integrations/pipedrive) into a
 * single reactive map: provider → boolean.
 *
 * The first call to isIntegrationEnabled() triggers the fetch automatically.
 * Once loaded, the reactive state causes any computed / template that read
 * from it to re-evaluate.
 *
 * Usage:
 *   const { isIntegrationEnabled } = useIntegrationStatus()
 *   isIntegrationEnabled('salesforce') // false initially, reactively updates
 */

const state = reactive<Record<string, boolean>>({})
let loaded = false
let pending: Promise<void> | null = null

function ensureLoaded(): void {
  if (loaded || pending) return
  pending = (async () => {
    const [wsResp, pdResp] = await Promise.allSettled([
      apiFetch('/settings/integrations'),
      apiFetch('/user/integrations/pipedrive'),
    ])

    if (wsResp.status === 'fulfilled') {
      for (const row of (wsResp.value?.integrations || []) as Array<{ provider?: string; configured?: boolean }>) {
        if (row.provider) state[row.provider] = Boolean(row.configured)
      }
    }

    if (pdResp.status === 'fulfilled') {
      state['pipedrive'] = Boolean(pdResp.value?.connected)
    }

    loaded = true
    pending = null
  })().catch(() => { pending = null })
}

export function useIntegrationStatus() {
  function isIntegrationEnabled(provider: string): boolean {
    ensureLoaded()
    return Boolean(state[provider])
  }

  /** Force a reload (e.g. after saving integration settings). */
  function reloadIntegrations(): void {
    loaded = false
    pending = null
    ensureLoaded()
  }

  return { isIntegrationEnabled, reloadIntegrations }
}
